/**
 * Inference pipeline types — shared between the signal client plugin and the
 * external-api server.  Kept in sync with:
 *   external-api/src/events/listeners/inference/types.ts
 */

export interface ClickData {
  timestamp: number;
  x: number;
  y: number;
}

export interface MousePosition {
  timestamp: number;
  x: number;
  y: number;
}

export interface ScrollData {
  timestamp: number;
  y: number;
}

export interface InputData {
  timestamp: number;
  text: string;
}

export interface FocusBlurData {
  timestamp: number;
  type: "focus" | "blur";
}

export interface MutationData {
  timestamp: number;
}

export interface TouchData {
  timestamp: number;
}

export interface SelectionData {
  timestamp: number;
}

export interface DragData {
  timestamp: number;
}

export interface StyleSheetData {
  timestamp: number;
}

export interface CanvasData {
  timestamp: number;
}

export interface ViewportData {
  width: number;
  height: number;
}

export interface InteractionBuffers {
  clicks: ClickData[];
  mousePositions: MousePosition[];
  scrolls: ScrollData[];
  inputs: InputData[];
  focusBlurs: FocusBlurData[];
  mutations: MutationData[];
  touches: TouchData[];
  selections: SelectionData[];
  drags: DragData[];
  styleSheets: StyleSheetData[];
  canvasEvents: CanvasData[];
  viewport: ViewportData;
  sessionStartTimestamp: number;
  latestTimestamp: number;
  latestHref: string | null;
}

export interface ElementData {
  tag: string;
  id?: string | null;
  className?: string | null;
  text?: string | null;
  href?: string | null;
  ariaLabel?: string | null;
  placeholder?: string | null;
  x: number;
  y: number;
  width: number;
  height: number;
  isVisible?: boolean;
  isInteractive?: boolean;
  isButton?: boolean;
  isInput?: boolean;
  isLink?: boolean;
  isFixed?: boolean;
  hasAnimation?: boolean;
  childCount?: number;
  depth?: number;
  parentIdx?: number;
}

export interface ClickPosition {
  x: number;
  y: number;
  timestamp: number;
}

export interface LiveAnalysisRequest {
  url: string;
  title?: string;
  elements: ElementData[];
  viewport: { width: number; height: number };
  behavioral_context: number[][];
  raw_metrics?: Record<string, unknown>;
  click_positions?: ClickPosition[];
}

export function createEmptyBuffers(): InteractionBuffers {
  return {
    clicks: [],
    mousePositions: [],
    scrolls: [],
    inputs: [],
    focusBlurs: [],
    mutations: [],
    touches: [],
    selections: [],
    drags: [],
    styleSheets: [],
    canvasEvents: [],
    viewport: { width: 1920, height: 1080 },
    sessionStartTimestamp: Infinity,
    latestTimestamp: -Infinity,
    latestHref: null,
  };
}
